import { jsPDF } from "jspdf";

const W = 595.28;
const H = 841.89;
const M = 52;
const maxW = W - M * 2;

const clean = (t) =>
  String(t)
    .replace(/″/g, '"')
    .replace(/[„“”«»]/g, '"')
    .replace(/[’‘]/g, "'")
    .replace(/–/g, "-")
    .replace(/…/g, "...")
    .replace(/[✦✨→]/g, "");

const makeDoc = () => {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  let page = 1;
  let y = 64;
  const footer = () => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(138, 127, 121);
    doc.text("MIRA MIRROR  ·  mira-mirror.ai", M, H - 30);
    doc.text(String(page), W - M, H - 30, { align: "right" });
  };
  const api = {
    doc,
    get y() { return y; },
    set y(v) { y = v; },
    newPage() {
      doc.addPage();
      page++;
      doc.setFillColor(252, 250, 248);
      doc.rect(0, 0, W, H, "F");
      footer();
      y = 64;
    },
    ensure(h) { if (y + h > H - 60) api.newPage(); },
    para(txt, size = 9, color = [90, 82, 78], style = "normal", indent = 0, lh = 12) {
      doc.setFont("helvetica", style);
      doc.setFontSize(size);
      doc.setTextColor(...color);
      doc.splitTextToSize(clean(txt), maxW - indent).forEach((ln) => {
        api.ensure(lh);
        doc.text(ln, M + indent, y);
        y += lh;
      });
    },
    heading(txt, size = 17) {
      api.ensure(40);
      y += 8;
      doc.setFont("times", "bold");
      doc.setFontSize(size);
      doc.setTextColor(44, 39, 37);
      doc.splitTextToSize(clean(txt), maxW).forEach((ln) => {
        api.ensure(size + 4);
        doc.text(ln, M, y);
        y += size + 4;
      });
      y += 4;
    },
    numbered(i, title, text) {
      api.ensure(50);
      doc.setFillColor(198, 156, 109);
      doc.circle(M + 7, y - 3, 7.5, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.setTextColor(255, 255, 255);
      doc.text(String(i), M + 7, y - 0.5, { align: "center" });
      doc.setFontSize(11);
      doc.setTextColor(44, 39, 37);
      doc.text(clean(title), M + 23, y);
      y += 15;
      api.para(text, 9.5, [90, 82, 78], "normal", 23, 12.5);
      y += 10;
    },
    cover(kicker, title, sub) {
      doc.setFillColor(38, 33, 28);
      doc.rect(0, 0, W, H, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(198, 156, 109);
      doc.text(clean(kicker).toUpperCase(), M, 120);
      doc.setDrawColor(198, 156, 109);
      doc.setLineWidth(1);
      doc.line(M, 134, M + 120, 134);
      doc.setFont("times", "bold");
      doc.setFontSize(32);
      doc.setTextColor(252, 250, 248);
      const tl = doc.splitTextToSize(clean(title), maxW);
      doc.text(tl, M, 215);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.setTextColor(210, 200, 190);
      doc.text(doc.splitTextToSize(clean(sub), maxW - 40), M, 215 + tl.length * 36 + 18);
      doc.setFontSize(8);
      doc.setTextColor(138, 127, 121);
      doc.text("MIRA MIRROR  ·  mira-mirror.ai", M, H - 46);
    },
    claim(txt) {
      api.ensure(50);
      y += 14;
      doc.setFont("times", "bolditalic");
      doc.setFontSize(12.5);
      doc.setTextColor(198, 156, 109);
      doc.splitTextToSize('"' + clean(txt) + '"', maxW - 60).forEach((ln) => {
        api.ensure(16);
        doc.text(ln, W / 2, y, { align: "center" });
        y += 16;
      });
    },
  };
  return api;
};

export const buildStyleGuide = (t) => {
  const lm = t.news.leadMagnet;
  const p = makeDoc();
  p.cover("Mira Mini-Guide", lm.guideTitle, lm.guideSub);
  p.newPage();
  p.heading(lm.guideTitle, 19);
  p.para(lm.guideSub, 10, [90, 82, 78], "oblique", 0, 13);
  p.y += 14;
  lm.tips.forEach((tip, i) => p.numbered(i + 1, tip.t, tip.d));
  p.claim(lm.claim);
  p.doc.save("mira-styling-tipps.pdf");
};

export const buildLookbook = (t, articles) => {
  const lm = t.news.leadMagnet;
  const m = new Date().getMonth();
  const season = m >= 3 && m <= 8 ? lm.seasonSS : lm.seasonAW;
  const p = makeDoc();
  p.cover(lm.lookbookTitle, `${lm.lookbookTitle} ${season} ${new Date().getFullYear()}`, lm.lookbookSub);
  articles.slice(0, 8).forEach((a) => {
    p.newPage();
    p.doc.setFont("helvetica", "bold");
    p.doc.setFontSize(8.5);
    p.doc.setTextColor(198, 156, 109);
    p.doc.text(clean(lm.fromMagazine).toUpperCase(), M, p.y);
    p.y += 18;
    p.heading(a.title, 16);
    if (a.excerpt) p.para(a.excerpt, 9.5, [90, 82, 78], "oblique", 0, 12.5);
    p.y += 8;
    (a.sections || []).forEach((s) => {
      p.heading(s.h, 12);
      p.para(s.p, 9, [90, 82, 78], "normal", 0, 11.5);
      p.y += 4;
    });
  });
  p.newPage();
  p.y = 340;
  p.claim(lm.claim);
  p.doc.setFont("helvetica", "bold");
  p.doc.setFontSize(12);
  p.doc.setTextColor(198, 156, 109);
  p.doc.text("mira-mirror.ai", W / 2, p.y + 24, { align: "center" });
  p.doc.save(`mira-lookbook-${season.replace("/", "-").toLowerCase()}-${new Date().getFullYear()}.pdf`);
};
